<script>
/**
 * DashboardLayout -- the single card-grouped dashboard skeleton (Hybrid A+B).
 *
 * Every role landing (built in Stories 1.4/1.5) composes this one wrapper so the
 * structure is never re-invented: a row of SummaryStatCards on top, a primary
 * list/queue region below. This story delivers the empty, reusable shell only --
 * no data, no route, not yet wired into any page.
 *
 * SLOT CONTRACT
 * -------------
 *   #summary  -> the summary stat cards. Pass BARE <SummaryStatCard> elements --
 *                NO Bootstrap col wrappers. The skeleton OWNS the grid: it wraps
 *                each card in its own `col-12 col-md-6 col-xl-3` column, so the
 *                24px token gutter (var(--space-6), not Bootstrap 4's 30px) AND
 *                the single-column reflow below the md (~768px) breakpoint are
 *                guaranteed regardless of the consumer (AC-5/AC-6). The row is
 *                optional -- omit the slot and only the primary region renders.
 *   default   -> the primary list/queue region (rendered below the summary row).
 *
 * A render function (not a <template>) is used deliberately: a <template> cannot
 * place a per-child column wrapper around opaque slot content, and AC-4 requires
 * the summary to be a slot. Slot vnodes are read via $scopedSlots, which IS
 * reactive in Vue 2.6 (unlike $slots), so a summary provided only after async
 * load still re-renders the row; whitespace/comment vnodes are filtered out so an
 * empty slot renders nothing (no stray gutter or vertical-rhythm gap).
 *
 * Content is constrained to a centered ~1280px max-width (AC-5). Tokens only --
 * no hardcoded hex, src/design/ untouched (AC-8).
 */
export default {
  name: "DashboardLayout",
  computed: {
    // Real (element/component) vnodes in the summary slot; whitespace/comment
    // nodes dropped. $scopedSlots is reactive in Vue 2.6, so a card row that
    // appears after data loads re-renders correctly.
    summaryCards() {
      const slot = this.$scopedSlots.summary
        ? this.$scopedSlots.summary()
        : this.$slots.summary;
      return (slot || []).filter(vnode => vnode.tag);
    }
  },
  render(h) {
    const children = [];

    if (this.summaryCards.length) {
      children.push(
        h(
          "div",
          { class: "dashboard-layout__summary" },
          this.summaryCards.map(card =>
            h(
              "div",
              { class: "dashboard-layout__col col-12 col-md-6 col-xl-3" },
              [card]
            )
          )
        )
      );
    }

    children.push(
      h("div", { class: "dashboard-layout__primary" }, this.$slots.default)
    );

    return h("div", { class: "dashboard-layout" }, children);
  }
};
</script>

<style scoped>
/* Centered, max-width-constrained dashboard body. Bootstrap 4's .container caps
   at 1140px (xl), so the spec's ~1280px needs this custom wrapper (AC-5). */
.dashboard-layout {
  max-width: 1280px;
  margin: 0 auto;
}

/* The summary stat-card row. We are the flex row and the skeleton renders the
   columns itself, so the gutter comes from the token: -12px row margin + 12px
   column padding = 24px gutters, horizontal AND vertical between wrapped cards
   (AC-5). Bootstrap 4 has no g-* utilities and defaults to 30px, hence the token
   override. */
.dashboard-layout__summary {
  display: flex;
  flex-wrap: wrap;
  margin: calc(var(--space-6) / -2);
}

/* Half-gutter padding on the columns the skeleton renders. Scoped to our OWN col
   class (no ::v-deep, no broad [class*="col"] substring match) so it can never
   leak onto nested or unrelated Bootstrap classes. The col-12 col-md-6 col-xl-3
   widths drive the md reflow to a single column (AC-6); min-width:0 lets a long
   card value wrap instead of forcing the column past its basis. */
.dashboard-layout__col {
  padding: calc(var(--space-6) / 2);
  min-width: 0;
}

/* Vertical rhythm between the summary row and the primary region (AC-5). */
.dashboard-layout__summary + .dashboard-layout__primary {
  margin-top: var(--space-6);
}
</style>
